/**
 * The scenario form in a modal.
 *
 * Thin: the form is `LabScenarioForm`, so the builder can open the same thing in a slide-over without
 * leaving the tree. What stays here is the draft, the save, and the errors.
 *
 * Authoring goes to toolbox_db's generic CRUD, not the lab controller - the controller has no
 * authoring endpoints, so that a half-written scenario cannot be distributed because somebody saved
 * it. Publishing is a separate, deliberate act.
 */

import React, { useEffect, useMemo, useState } from 'react';
import StbModal from '../security-toolbox-ui/StbModal';
import { useSaveLabRow } from '../../hooks/lab-hooks';
import { LabScenario, LAB_TABLES } from '../../support/labService';
import { labErrorMessage } from './LabShared';
import LabScenarioForm, {
  labScenarioFormBlockers,
  labScenarioSlug,
  LAB_SCENARIO_EMPTY,
} from './LabScenarioForm';

export type LabScenarioEditorModalProps = {
  open: boolean;
  scenario: LabScenario | null;
  onClose: () => void;
  onSaved?: (scenario: LabScenario) => void;
};

const LabScenarioEditorModal: React.FC<LabScenarioEditorModalProps> = ({
  open,
  scenario,
  onClose,
  onSaved,
}) => {
  const [draft, setDraft] = useState<Partial<LabScenario>>(LAB_SCENARIO_EMPTY);
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const save = useSaveLabRow<Partial<LabScenario> & { id?: number }>(LAB_TABLES.scenario);

  useEffect(() => {
    if (!open) return;

    setDraft(scenario ? { ...scenario } : { ...LAB_SCENARIO_EMPTY });
    setJsonError(null);
    setFormError(null);
    save.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, scenario?.id]);

  const set = (patch: Partial<LabScenario>) => {
    setFormError(null);
    setDraft((current) => ({ ...current, ...patch }));
  };

  const generatedSlug = useMemo(() => labScenarioSlug(scenario, draft), [scenario, draft]);
  const saveBlockers = useMemo(
    () => labScenarioFormBlockers(draft, generatedSlug),
    [draft, generatedSlug],
  );

  const handleSubmit = async () => {
    if (saveBlockers.length) {
      setFormError(saveBlockers[0]);
      return;
    }

    if (jsonError) {
      setFormError(`Metadata: ${jsonError}`);
      return;
    }

    // Every lab in the catalogue is set at PseudoCo, and the definition loader refuses any other
    // customer. Saying so here beats a 400 from the loader weeks later.
    const customer = String((draft.metadata as any)?.customer || '');

    if (customer && customer !== 'PseudoCo') {
      setFormError(
        `Labs are set at PseudoCo; metadata.customer says "${customer}". The scenario loader will refuse this.`,
      );
      return;
    }

    setFormError(null);

    try {
      const saved = await save.mutateAsync({ ...draft, slug: generatedSlug });
      onSaved?.(saved as LabScenario);
      onClose();
    } catch {
      // Surfaced through save.error.
    }
  };

  const submitError = formError || (save.isError ? labErrorMessage(save.error) : null);

  return (
    <StbModal
      open={open}
      role="update"
      title={scenario ? `Edit ${scenario.name}` : 'New lab section'}
      size="large"
      scrollable
      submitLabel={scenario ? 'Save section' : 'Create section'}
      submitDisabled={!!saveBlockers.length || !!jsonError || save.isPending}
      suppressCloseOnSubmit
      onSubmit={handleSubmit}
      onClose={async () => onClose()}
      apiResponseSection={
        submitError ? (
          <p className="m-0 text-sm text-amber-800">{submitError}</p>
        ) : saveBlockers.length ? (
          <ul className="m-0 list-disc pl-5 text-sm text-gray-600">
            {saveBlockers.map((blocker) => (
              <li key={blocker}>{blocker}</li>
            ))}
          </ul>
        ) : undefined
      }
    >
      <LabScenarioForm
        scenario={scenario}
        draft={draft}
        onChange={set}
        active={open}
        jsonError={jsonError}
        onJsonError={setJsonError}
      />
    </StbModal>
  );
};

export default LabScenarioEditorModal;
